import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/blog/${slug}`)
      .then(r => {
        setPost(r.data);
        return axios.get(`${API}/blog`);
      })
      .then(r => setRelated(r.data.filter(p => p.slug !== slug).slice(0, 3)))
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return ''; }
  };

  if (loading) return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full" />
    </div>
  );

  if (notFound) return (
    <div className="pt-20 min-h-screen flex items-center justify-center text-center px-4">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 font-heading mb-4">Article Not Found</h1>
        <p className="text-gray-600 mb-8">This article doesn't exist or may have been removed.</p>
        <Link to="/blog" className="btn-primary">Back to Blog</Link>
      </div>
    </div>
  );

  if (!post) return null;

  return (
    <div className="pt-20">
      {/* BACK NAV */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/blog" data-testid="blog-back-btn" className="flex items-center gap-2 text-gray-500 text-sm hover:text-brand-red transition-colors">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
        </div>
      </div>

      {/* HERO */}
      <div className="relative">
        <img src={post.image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80'} alt={post.title}
          className="w-full h-72 md:h-96 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-gray-900/20" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="bg-brand-red text-white text-xs px-3 py-1 font-semibold uppercase tracking-wider flex items-center gap-1">
                  <Tag size={11} /> {post.category}
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1.5"><Calendar size={13} />{formatDate(post.created_at)}</span>
                <span className="text-gray-300 text-sm flex items-center gap-1.5"><User size={13} />{post.author}</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading tracking-tight leading-tight" data-testid="post-title">
                {post.title}
              </h1>
            </motion.div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                <p className="text-xl text-gray-600 leading-relaxed mb-8 border-l-4 border-brand-red pl-6 italic">{post.excerpt}</p>
                <div
                  className="blog-content prose max-w-none"
                  data-testid="post-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              </motion.div>
            </div>

            {/* SIDEBAR */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-gray-50 border border-gray-200 p-6">
                <h4 className="font-bold text-gray-900 font-heading mb-4">About the Author</h4>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-brand-red flex items-center justify-center text-white font-bold text-sm">
                    {post.author?.charAt(0) || 'S'}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{post.author}</p>
                    <p className="text-gray-500 text-xs">Seva Gases Expert</p>
                  </div>
                </div>
              </div>

              <div className="bg-brand-red p-6">
                <h4 className="font-bold text-white font-heading mb-3">Need Gas Solutions?</h4>
                <p className="text-red-100 text-sm mb-5">Our experts are ready to help with your industrial gas requirements.</p>
                <Link to="/contact" data-testid="post-cta-btn" className="block bg-white text-brand-red text-center py-2.5 text-sm font-semibold hover:bg-gray-100 transition-colors">
                  Get a Quote
                </Link>
              </div>

              <div className="border border-gray-200 p-6">
                <h4 className="font-bold text-gray-900 font-heading mb-3 text-sm uppercase tracking-wider">Category</h4>
                <span className="section-tag">{post.category}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED POSTS */}
      {related.length > 0 && (
        <section className="py-16 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 font-heading mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`related-post-${i}`}
                  className="group bg-white border border-gray-200 hover:border-brand-red transition-all duration-200">
                  <img src={p.image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&q=80'} alt={p.title}
                    className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="p-5">
                    <span className="section-tag mb-2 block">{p.category}</span>
                    <h3 className="font-bold text-gray-900 font-heading text-base group-hover:text-brand-red transition-colors line-clamp-2">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
